import axios from 'axios';
import config from '../config/api';

const API_URL = config.endpoints.auth;

// Clave para almacenar el token en localStorage
const TOKEN_KEY = 'fepast_auth_token';
const USER_KEY = 'fepast_user_data';

class AuthService {
  /**
   * Iniciar sesión
   * @param {string} username - Nombre de usuario (cédula)
   * @param {string} password - Contraseña
   * @returns {Promise<Object>} Datos del usuario y token
   */
  async login(username, password) {
    try {
      const response = await axios.post(`${API_URL}/login`, {
        username,
        password
      });

      if (response.data && response.data.token) {
        // Guardar token y datos del usuario en localStorage
        localStorage.setItem(TOKEN_KEY, response.data.token);
        localStorage.setItem(USER_KEY, JSON.stringify(response.data.usuario));
      }

      return response.data;
    } catch (error) {
      console.error('Error en login:', error);
      throw error.response?.data?.error || 'Error al iniciar sesión';
    }
  }

  /**
   * Cerrar sesión
   */
  logout() {
    // Llamada al servidor para invalidar sesión (si existe)
    const token = this.getToken();
    if (token) {
      // Intentamos cerrar de forma asíncrona; no esperamos respuesta.
      fetch(`${API_URL}/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({}),
        keepalive: true
      }).catch(() => {});
    }

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  /**
   * Enviar logout al servidor cuando la pestaña/ventana se cierra.
   * Usamos fetch con keepalive si está disponible; como fallback usamos navigator.sendBeacon.
   */
  async logoutOnUnload() {
    const token = this.getToken();
    if (!token) return;

    try {
      // fetch con keepalive es lo ideal
      await fetch(`${API_URL}/logout-beacon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
        keepalive: true
      });
    } catch (err) {
      // fallback a sendBeacon (no permite headers personalizados)
      try {
        const payload = JSON.stringify({ token });
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(`${API_URL}/logout-beacon`, blob);
      } catch (e) {
        // nada más que hacer
      }
    }
    // Finalmente limpiar localStorage local
    this.logout();
  }

  /**
   * Obtener token actual
   * @returns {string|null} Token JWT
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Obtener datos del usuario actual
   * @returns {Object|null} Datos del usuario
   */
  getCurrentUser() {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Verificar si el usuario está autenticado
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Verificar si el usuario tiene un permiso específico
   * @param {string} permiso - Nombre del permiso
   * @returns {boolean}
   */
  hasPermission(permiso) {
    const user = this.getCurrentUser();
    if (!user || !user.permisos) return false;
    // Tratar al administrador como superusuario: si tiene 'Administrador', concede cualquier permiso
    const permisos = user.permisos;
    const tieneAdministrador = permisos.some(p => {
      if (typeof p === 'string') return p === 'Administrador';
      if (p && typeof p === 'object') {
        const nombre = p.PERMISO_NOMBRE || p.permiso_nombre || p.PERMISOS_NOMBRE || p.nombre || p.name;
        return nombre === 'Administrador';
      }
      return false;
    });
    if (tieneAdministrador) return true;

    // Soporta arrays de strings o de objetos
    return permisos.some(p => {
      if (typeof p === 'string') {
        return p === permiso;
      }
      if (p && typeof p === 'object') {
        const nombre = p.PERMISO_NOMBRE || p.permiso_nombre || p.PERMISOS_NOMBRE || p.nombre || p.name;
        return nombre === permiso;
      }
      return false;
    });
  }

  /**
   * Verificar si el usuario tiene alguno de los permisos especificados
   * @param {string[]} permisos - Array de nombres de permisos
   * @returns {boolean}
   */
  hasAnyPermission(permisos) {
    if (!Array.isArray(permisos) || permisos.length === 0) return false;
    return permisos.some(permiso => this.hasPermission(permiso));
  }

  /**
   * Configurar interceptor de axios para incluir token en todas las peticiones
   */
  setupAxiosInterceptors() {
    axios.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor de respuesta para manejar errores 401 (no autorizado)
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expirado o inválido, cerrar sesión
          this.logout();
          window.location.href = '/login'; // Redirigir al login
        }
        return Promise.reject(error);
      }
    );
  }
}

export default new AuthService();
